/// <reference lib="dom" />
import { isBrowserEnvironment } from '@openai/agents-core/_shims';
import { OpenAIRealtimeBase, UserError, } from '@openai/agents/realtime';
import { parseRealtimeEvent } from './transportHelper';
/**
 * Custom transport layer that includes the option to configure TURN and ice servers
 * Based on official implementation here https://github.com/openai/openai-agents-js/blob/main/packages/agents-realtime/src/openaiRealtimeWebRtc.ts
 */
export class CustomOpenAIRealtimeWebRTC extends OpenAIRealtimeBase {
    options;
    _url;
    _state = {
        status: 'disconnected',
        peerConnection: undefined,
        dataChannel: undefined,
    };
    _useInsecureApiKey;
    _ongoingResponse = false;
    _muted = false;
    constructor(options) {
        if (typeof RTCPeerConnection === 'undefined') {
            throw new Error('WebRTC is not supported in this environment');
        }
        super(options);
        this.options = options;
        this._url = options.baseUrl ?? `https://api.openai.com/v1/realtime`;
        this._useInsecureApiKey = options.useInsecureApiKey ?? false;
    }
    /**
     * The current status of the WebRTC connection.
     */
    get status() {
        return this._state.status;
    }
    /**
     * The current connection state of the WebRTC connection including the peer connection and data
     * channel.
     */
    get connectionState() {
        return this._state;
    }
    /**
     * Whether the session is muted.
     */
    get muted() {
        return this._muted;
    }
    /**
     * Connect to the Realtime API. This will establish the connection to the OpenAI Realtime API
     * via WebRTC.
     *
     * If you are using a browser, the transport layer will also automatically configure the
     * microphone and audio output to be used by the session.
     *
     * @param options - The options for the connection.
     */
    async connect(options) {
        if (this._state.status === 'connected') {
            return;
        }
        if (this._state.status === 'connecting') {
            console.warn('Realtime connection already in progress. Please await original promise');
        }
        const model = options.model ?? this.currentModel;
        this.currentModel = model;
        const baseUrl = options.url ?? this._url;
        const apiKey = await this._getApiKey(options);
        const isClientKey = typeof apiKey === 'string' && apiKey.startsWith('ek_');
        if (isBrowserEnvironment() && !this._useInsecureApiKey && !isClientKey) {
            throw new UserError('Using the WebRTC connection in a browser environment requires an insecure API key. Please use a WebSocket connection instead or set the useInsecureApiKey option to true.');
        }
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
            try {
                const userSessionConfig = {
                    ...(options.initialSessionConfig || {}),
                    model: this.currentModel,
                };
                const connectionUrl = new URL(baseUrl);
                let peerConnection = new RTCPeerConnection({
                    iceServers: this.options.iceServers,
                });
                // Debug ICE candidates
                peerConnection.onicecandidate = (event) => {
                    if (event.candidate && (this.options.debugIceServers ?? false)) {
                        console.log('🧊 ICE Candidate:', event.candidate.candidate);
                        if (event.candidate.candidate.includes('typ relay')) {
                            console.log('🎉 TURN/RELAY candidate found!', event.candidate);
                        }
                        else if (event.candidate.candidate.includes('typ srflx')) {
                            console.log('🌐 STUN candidate found!', event.candidate);
                        }
                        else if (event.candidate.candidate.includes('typ host')) {
                            console.log('🏠 HOST candidate found!', event.candidate);
                        }
                    }
                };
                // Debug ICE connection state
                peerConnection.oniceconnectionstatechange = () => {
                    if (this.options.debugIceServers ?? false) {
                        console.log('🔄 ICE Connection State:', peerConnection.iceConnectionState);
                    }
                };
                // Debug ICE gathering state
                peerConnection.onicegatheringstatechange = () => {
                    if (this.options.debugIceServers ?? false) {
                        console.log('📡 ICE Gathering State:', peerConnection.iceGatheringState);
                        if (peerConnection.iceGatheringState === 'complete') {
                            console.log('✅ ICE gathering complete - all candidates gathered');
                        }
                    }
                };
                // Debug ICE candidate errors
                peerConnection.addEventListener('icecandidateerror', (event) => {
                    if (this.options.debugIceServers ?? false) {
                        console.error('❌ ICE Candidate Error:', {
                            url: event.url,
                            errorCode: event.errorCode,
                            errorText: event.errorText,
                            address: event.address,
                            port: event.port,
                        });
                    }
                });
                // Debug TURN server configuration (only show if debug enabled)
                if (this.options.debugIceServers ?? false) {
                    console.log('🎯 TURN Servers configured:', this.options.iceServers);
                    this.options.iceServers.forEach((server, index) => {
                        console.log(`🎯 TURN Server ${index + 1}:`, {
                            urls: server.urls,
                            username: server.username?.substring(0, 20) + '...',
                            credential: server.credential ? '***masked***' : 'missing',
                        });
                    });
                    // Log ICE server configuration type
                    const hasStunOnly = this.options.iceServers.every((server) => typeof server.urls === 'string'
                        ? server.urls.startsWith('stun:')
                        : Array.isArray(server.urls)
                            ? server.urls.every((url) => url.startsWith('stun:'))
                            : false);
                    const hasTurn = this.options.iceServers.some((server) => typeof server.urls === 'string'
                        ? server.urls.startsWith('turn:')
                        : Array.isArray(server.urls)
                            ? server.urls.some((url) => url.startsWith('turn:'))
                            : false);
                    if (hasStunOnly && !hasTurn) {
                        console.log('🧪 Using STUN-only configuration (good for testing)');
                    }
                    else if (hasTurn) {
                        console.log('🔄 Using TURN+STUN configuration (production setup)');
                    }
                }
                const dataChannel = peerConnection.createDataChannel('oai-events');
                this._state = {
                    status: 'connecting',
                    peerConnection,
                    dataChannel,
                };
                this.emit('connection_change', this._state.status);
                dataChannel.addEventListener('open', () => {
                    this._state = {
                        status: 'connected',
                        peerConnection,
                        dataChannel,
                    };
                    // Sending the session config again here once the channel is connected to ensure
                    // that the session config is sent to the server before the first response is received
                    // Setting it on connection should work but the config is not being validated on the
                    // server. This triggers a validation error if the config is not valid.
                    this.updateSessionConfig(userSessionConfig);
                    this.emit('connection_change', this._state.status);
                    this._onOpen();
                    resolve();
                });
                dataChannel.addEventListener('error', (event) => {
                    this.close();
                    this._onError(event);
                    reject(event);
                });
                dataChannel.addEventListener('message', (event) => {
                    this._onMessage(event);
                    const { data: parsed, isGeneric } = parseRealtimeEvent(event);
                    if (!parsed || isGeneric) {
                        return;
                    }
                    if (parsed.type === 'response.created') {
                        this._ongoingResponse = true;
                    }
                    else if (parsed.type === 'response.done') {
                        this._ongoingResponse = false;
                    }
                    if (parsed.type === 'session.created') {
                        this._tracingConfig = parsed.session.tracing;
                        // Trying to turn on tracing after the session is created
                        this._updateTracingConfig(userSessionConfig.tracing ?? 'auto');
                    }
                    if (parsed.type === 'output_audio_buffer.started') {
                        this.emit('output_audio_buffer_started');
                    }
                    if (parsed.type === 'output_audio_buffer.stopped') {
                        this.emit('output_audio_buffer_stopped');
                    }
                });
                // set up audio playback
                const audioElement = this.options.audioElement ?? document.createElement('audio');
                audioElement.autoplay = true;
                peerConnection.ontrack = (event) => {
                    const source = event.streams[0];
                    if (!source) {
                        throw new Error('No audio stream found');
                    }
                    audioElement.srcObject = source;
                };
                // get microphone stream
                const stream = this.options.mediaStream ??
                    (await navigator.mediaDevices.getUserMedia({
                        audio: true,
                    }));
                const track = stream.getAudioTracks()[0];
                if (!track) {
                    throw new Error('No audio track found');
                }
                peerConnection.addTrack(track);
                if (this.options.changePeerConnection) {
                    peerConnection =
                        await this.options.changePeerConnection(peerConnection);
                    this._state = { ...this._state, peerConnection };
                }
                const offer = await peerConnection.createOffer();
                await peerConnection.setLocalDescription(offer);
                if (!offer.sdp) {
                    throw new Error('Failed to create offer');
                }
                const sessionConfig = {
                    ...this._getMergedSessionConfig(userSessionConfig),
                    model: this.currentModel,
                };
                const data = new FormData();
                data.append('sdp', offer.sdp);
                data.append('session', JSON.stringify(sessionConfig));
                const sdpResponse = await fetch(connectionUrl, {
                    method: 'POST',
                    body: data,
                    headers: {
                        Authorization: `Bearer ${apiKey}`,
                        // 'X-OpenAI-Agents-SDK': HEADERS['X-OpenAI-Agents-SDK'],
                    },
                });
                const answer = {
                    type: 'answer',
                    sdp: await sdpResponse.text(),
                };
                await peerConnection.setRemoteDescription(answer);
            }
            catch (error) {
                this.close();
                this._onError(error);
                reject(error);
            }
        });
    }
    /**
     * Send an event to the Realtime API. This will stringify the event and send it directly to the
     * API. This can be used if you want to take control over the connection and send events manually.
     *
     * @param event - The event to send.
     */
    sendEvent(event) {
        if (!this._state.dataChannel || this._state.dataChannel.readyState !== 'open') {
            throw new Error('WebRTC data channel is not connected. Make sure you call `connect()` before sending events.');
        }
        this._state.dataChannel.send(JSON.stringify(event));
    }
    /**
     * Mute or unmute the session.
     * @param muted - Whether to mute the session.
     */
    mute(muted) {
        this._muted = muted;
        if (this._state.peerConnection) {
            const peerConnection = this._state.peerConnection;
            peerConnection.getSenders().forEach((sender) => {
                if (sender.track) {
                    sender.track.enabled = !muted;
                }
            });
        }
    }
    /**
     * Close the connection to the Realtime API and disconnects the underlying WebRTC connection.
     */
    close() {
        if (this._state.dataChannel) {
            this._state.dataChannel.close();
        }
        if (this._state.peerConnection) {
            const peerConnection = this._state.peerConnection;
            peerConnection.getSenders().forEach((sender) => {
                sender.track?.stop();
            });
            peerConnection.close();
        }
        if (this._state.status !== 'disconnected') {
            this._state = {
                status: 'disconnected',
                peerConnection: undefined,
                dataChannel: undefined,
            };
            this.emit('connection_change', this._state.status);
            this._onClose();
        }
    }
    /**
     * Interrupt the current response if one is ongoing and clear the audio buffer so that the agent
     * stops talking.
     */
    interrupt() {
        if (this._ongoingResponse) {
            this.sendEvent({
                type: 'response.cancel',
            });
            this._ongoingResponse = false;
        }
        this.sendEvent({
            type: 'output_audio_buffer.clear',
        });
    }
}
//# sourceMappingURL=transportProvider.js.map